// @ts-nocheck
import { useState } from 'react';
import {
  Box,
  Typography,
  FormHelperText,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MuiTelInput } from 'mui-tel-input';
import { XGInput, XGTimePicker } from 'xg-cl';
import LineItemImageManager from '../LineItemImageManager/LineItemImageManager';
import LineItemAttachmentManager from '../LineItemAttachmentManager/LineItemAttachmentManager';
import Editor from '../../Editor/Editor';
import AIGenerateButton from '../../AIGenerateButton/AIGenerateButton';
import CheckBoxGroup from '../../CheckBoxGroup/CheckBoxGroup';
import { useTranslation } from 'react-i18next';
import { mealsIncludedOptions } from '../../../constants/lineItemFieldConfig';
import {
  maxImageSize,
  maxFileSize,
  maxImages,
  maxAttachments,
  STOCK_PHOTO_MAX_IMAGE_SIZE
} from '../../../constants/files';
import { normalizeLineItemDurationToHHmm } from '../../../utils/dateUtility';
import * as PropTypes from 'prop-types';
import { getGenericAIDescription } from '../../../services/ai/ai';
import { useFlags } from 'launchdarkly-react-client-sdk';

/**
 * LineItemFieldRenderer - Renders individual form fields based on field type
 *
 * @param {Object} fieldDef - Field definition from lineItemFieldConfig
 * @param {*} value - Current field value
 * @param {Function} onChange - Change handler
 * @param {Function} onError - Error handler
 * @param {string} error - Error message
 * @param {boolean} disabled - Whether field is disabled
 * @param {boolean} expanded - Whether accordion is expanded (for accordion fields)
 * @param {Function} onAccordionChange - Accordion toggle handler
 * @param {number} tripId - Trip ID (for file uploads)
 * @param {number} tenantId - Tenant ID (for file uploads)
 * @param {boolean} isLibrary - Whether this is a library item
 */
const LineItemFieldRenderer = ({
  fieldDef,
  value,
  onChange,
  onError,
  error = '',
  disabled = false,
  expanded = false,
  onAccordionChange,
  tripId,
  inputRef,
  isLibrary = false
}) => {
  const { t } = useTranslation(['common', 'trip']);
  const { enableStockPhotoPicker = false } = useFlags();
  const { field, label, type, required, accordion, maxLength, placeholder } =
    fieldDef;
  const fieldLabel = `${label}${required ? '*' : ''}`;
  const fieldId = `line-item-field-${field}`;
  const [isFocused, setIsFocused] = useState(
    inputRef?.current === document.activeElement
  );

  // Track whether the richtext editor is focused to avoid refreshing it while the user is typing (which causes cursor jumping).
  // We still want to refresh the editor when content is being generated by AI or updated by clicking on a different lineitem, so we track that separately.
  // XGE-2495
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // LineItem time fields are stored as timezone-agnostic strings in 24h format: "HH:mm".
  // We intentionally keep this logic local to LineItem editing (do not reuse legacy trip service utilities).
  const normalizeLineItemTimeToHHmm = (rawValue) => {
    if (!rawValue || typeof rawValue !== 'string') return null;

    const trimmed = rawValue.trim();

    // Preferred format: HH:mm
    if (/^\d{2}:\d{2}$/.test(trimmed)) return trimmed;

    // Accept HH:mm:ss
    const hhmmssMatch = trimmed.match(/^(\d{2}):(\d{2}):\d{2}$/);
    if (hhmmssMatch) return `${hhmmssMatch[1]}:${hhmmssMatch[2]}`;

    // Backward compatibility: legacy ISO strings (e.g., 1970-01-01T09:30:00.000Z)
    // Extract time directly from the string (no Date parsing -> no timezone conversion).
    const isoMatch = trimmed.match(/T(\d{2}):(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}:${isoMatch[2]}`;

    return null;
  };

  const lineItemHHmmToLocalDate = (hhmm) => {
    const normalized = normalizeLineItemTimeToHHmm(hhmm);
    if (!normalized) return null;

    const [hh, mm] = normalized.split(':').map((segment) => Number(segment));
    if ([hh, mm].some((n) => Number.isNaN(n))) return null;

    // Anchor date is arbitrary; we want the user-selected wall-clock time.
    // Use local Date so the picker shows exactly HH:mm.
    return new Date(1970, 0, 1, hh, mm, 0, 0);
  };

  const localDateToLineItemHHmm = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  const handleChange = (newValue, isFromAI = false) => {
    onChange(field, newValue);

    if (isFromAI) {
      setTimeout(() => {
        setIsGenerating(false);
      }, 500);
    }
  };

  const handleError = (errorMessage) => {
    if (onError) {
      onError(field, errorMessage);
    }
  };

  const handleEditorBlur = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    setIsEditorFocused(false);
  };

  const getRemainingCharsHint = (value = '') => {
    if (maxLength) {
      return maxLength
        ? `${value?.trim().length ?? 0}/${maxLength} ${t('charactersRemaing')}`
        : '';
    }
    return '';
  };

  // Boolean checkbox field (single checkbox)
  if (type === 'boolean') {
    const checked = Boolean(value);

    return (
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              id={fieldId}
              checked={checked}
              onChange={(e) => handleChange(e.target.checked)}
              disabled={disabled}
            />
          }
          label={fieldLabel}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </Box>
    );
  }

  // Text input field
  if (type === 'text') {
    if (field === 'title') {
      return (
        <Box style={{ position: 'relative' }}>
          <Typography className='xg-input-label'>{fieldLabel}</Typography>
          <XGInput
            id={fieldId}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            error={!!error}
            hint={error}
            disabled={disabled}
            fullWidth
            placeholder={placeholder || ''}
            inputProps={{
              maxLength: maxLength ?? null
            }}
            inputRef={inputRef}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
          {maxLength && isFocused && (
            <span className='title-length-hint length-hint text-sm-medium'>
              {getRemainingCharsHint(value || '')}
            </span>
          )}
        </Box>
      );
    }

    return (
      <Box>
        <XGInput
          id={fieldId}
          label={fieldLabel}
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          error={!!error}
          helperText={error}
          disabled={disabled}
          fullWidth
          placeholder={placeholder || ''}
          inputProps={{
            maxLength: maxLength ?? null
          }}
        />
      </Box>
    );
  }

  // Number input field
  if (type === 'number') {
    return (
      <Box>
        <XGInput
          id={fieldId}
          label={fieldLabel}
          type='text'
          inputMode='numeric'
          value={value || ''}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || (!isNaN(val) && Number(val) >= 0)) {
              handleChange(val);
            }
          }}
          error={!!error}
          helperText={error}
          disabled={disabled}
          fullWidth
          inputProps={{ min: 0 }}
          placeholder={placeholder || ''}
        />
      </Box>
    );
  }

  // Phone input field
  if (type === 'phone') {
    return (
      <Box>
        <Typography className='xg-input-label'>{fieldLabel}</Typography>
        <MuiTelInput
          id={fieldId}
          value={value || ''}
          onChange={(newValue) => handleChange(newValue)}
          error={!!error}
          helperText={error}
          disabled={disabled}
          fullWidth
          defaultCountry='US'
        />
      </Box>
    );
  }

  // URL input field
  if (type === 'url') {
    return (
      <Box>
        <XGInput
          id={fieldId}
          label={fieldLabel}
          type='url'
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          error={!!error}
          helperText={error}
          disabled={disabled}
          fullWidth
          placeholder={placeholder || 'https://'}
        />
      </Box>
    );
  }

  // Time input field
  if (type === 'time') {
    return (
      <Box>
        <XGTimePicker
          id={fieldId}
          label={fieldLabel}
          value={lineItemHHmmToLocalDate(value)}
          format={'HH:mm'}
          ampm={false}
          onChange={(newValue) => {
            const timeString = newValue
              ? localDateToLineItemHHmm(newValue)
              : null;
            handleChange(timeString);
          }}
          error={!!error}
          helperText={error}
          disabled={disabled}
          fullWidth
        />
      </Box>
    );
  }

  // Duration input field (HH:mm). This is not a time-of-day, so we avoid TimePicker/AMPM.
  if (type === 'duration') {
    const normalizedDuration = normalizeLineItemDurationToHHmm(value);

    const [storedHours = '', storedMinutes = ''] = (normalizedDuration || '')
      .split(':')
      .slice(0, 2);

    const displayHours =
      storedHours !== '' && !Number.isNaN(Number(storedHours))
        ? String(Number(storedHours))
        : '';
    const displayMinutes =
      storedMinutes !== '' && !Number.isNaN(Number(storedMinutes))
        ? String(Number(storedMinutes))
        : '';

    const coerceHours = (raw) => {
      if (raw === '' || raw === null || raw === undefined) return '';
      const n = Number(raw);
      if (Number.isNaN(n) || n < 0) return '';
      return String(Math.floor(n));
    };

    const coerceMinutes = (raw) => {
      if (raw === '' || raw === null || raw === undefined) return '';
      const n = Number(raw);
      if (Number.isNaN(n) || n < 0) return '';
      return String(Math.min(Math.floor(n), 59));
    };

    const buildDuration = (hoursRaw, minutesRaw) => {
      const h = coerceHours(hoursRaw);
      const m = coerceMinutes(minutesRaw);

      if (h === '' && m === '') return '';

      const hh = String(Number(h || 0)).padStart(2, '0');
      const mm = String(Number(m || 0)).padStart(2, '0');
      return `${hh}:${mm}`;
    };

    return (
      <Box>
        <Typography className='xg-input-label'>{fieldLabel}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <XGInput
              id={`${fieldId}-hours`}
              value={displayHours}
              onChange={(e) => {
                const nextHours = e.target.value;
                handleChange(buildDuration(nextHours, displayMinutes));
              }}
              onBlur={(e) => {
                const nextHours = e.target.value;
                handleChange(buildDuration(nextHours, displayMinutes));
              }}
              disabled={disabled}
              type='text'
              inputMode='numeric'
              inputProps={{ min: 0 }}
              fullWidth
              placeholder={'00'}
            />
          </Box>
          <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
            Hours
          </Typography>
          <Box sx={{ flex: 1 }}>
            <XGInput
              id={`${fieldId}-minutes`}
              value={displayMinutes}
              onChange={(e) => {
                const nextMinutes = e.target.value;
                handleChange(buildDuration(displayHours, nextMinutes));
              }}
              onBlur={(e) => {
                const nextMinutes = e.target.value;
                handleChange(buildDuration(displayHours, nextMinutes));
              }}
              disabled={disabled}
              type='text'
              inputMode='numeric'
              inputProps={{ min: 0, max: 59 }}
              fullWidth
              placeholder={'00'}
            />
          </Box>
          <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
            Minutes
          </Typography>
        </Box>
        {error && <FormHelperText error>{error}</FormHelperText>}
      </Box>
    );
  }

  // Textarea/Editor field
  if (type === 'textarea') {
    // If it's an accordion field (internal note), wrap in accordion
    if (accordion) {
      return (
        <Accordion
          expanded={expanded}
          onChange={onAccordionChange}
          className='line-item-edit-slideout__accordion'
          disableGutters
          elevation={0}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className='line-item-edit-slideout__accordion-summary'
          >
            <Typography className='line-item-edit-slideout__section-title'>
              {fieldLabel}
            </Typography>
          </AccordionSummary>
          <AccordionDetails className='line-item-edit-slideout__accordion-details'>
            <Box
              onFocus={() => setIsEditorFocused(true)}
              onBlur={handleEditorBlur}
            >
              <Editor
                id={fieldId}
                content={value || ''}
                setMarkdown={(newValue) => {
                  // Only update if value actually changed to prevent cursor jumping
                  if (newValue !== value) {
                    handleChange(newValue);
                  }
                }}
                editable={!disabled}
                isPreviewing={false}
                showActions={false}
                placeholder={t('trip:enterInternalNotes')}
                generatePrompt={null}
                isEditorDisabled={disabled}
                refreshOnChange={!isEditorFocused}
                maxLength={maxLength}
              />
            </Box>
            {error && <FormHelperText error>{error}</FormHelperText>}
          </AccordionDetails>
        </Accordion>
      );
    }

    // Regular textarea (description) with AI helper
    return (
      <Box>
        <Typography className='xg-input-label'>{fieldLabel}</Typography>
        <Box onFocus={() => setIsEditorFocused(true)} onBlur={handleEditorBlur}>
          <Editor
            id={fieldId}
            content={value || ''}
            setMarkdown={(newValue) => {
              // Only update if value actually changed to prevent cursor jumping
              if (newValue !== value) {
                handleChange(newValue);
              }
            }}
            editable={!disabled}
            isPreviewing={false}
            showActions={false}
            placeholder={t('trip:enterDescription')}
            generatePrompt={
              <AIGenerateButton
                id={`ai-generate-${fieldId}`}
                tripId={tripId}
                editorContent={value || ''}
                setMarkdown={(desc) => handleChange(desc, true)}
                setTripDescription={(desc) => handleChange(desc, true)}
                getAIDescription={getGenericAIDescription}
                setExtenalIsGenerating={setIsGenerating}
                disableGenerate={true}
              />
            }
            isEditorDisabled={isGenerating || disabled}
            isEditorGenerating={isGenerating}
            refreshOnChange={!isEditorFocused || isGenerating}
            maxLength={maxLength}
          />
        </Box>
        {error && <FormHelperText error>{error}</FormHelperText>}
      </Box>
    );
  }

  // Checkbox field (meals included)
  if (type === 'checkbox') {
    const checkboxOptions = (mealsIncludedOptions || []).map((opt, index) => {
      const optionId = opt?.value ?? opt?.label ?? String(index + 1);
      return {
        optionId,
        value: opt?.label ?? String(optionId),
        sequence: index + 1
      };
    });

    const selectedValues = Array.isArray(value) ? value : [];
    const selectedOptions = checkboxOptions.filter((opt) =>
      selectedValues.includes(opt.optionId)
    );

    return (
      <Box className='checkbox-group'>
        <CheckBoxGroup
          id={fieldId}
          groupKey={fieldId}
          fieldId={fieldId}
          label={label}
          options={checkboxOptions}
          selectedOptions={selectedOptions}
          handleOnChange={(_, __, updatedOptions = []) => {
            const nextValues = updatedOptions.map((opt) => opt.optionId);
            handleChange(nextValues);
          }}
          disabled={disabled}
          displayDirection='column'
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </Box>
    );
  }

  // Image upload field
  if (type === 'image') {
    return (
      <Box>
        <LineItemImageManager
          images={value || []}
          onChange={handleChange}
          onError={handleError}
          disabled={disabled}
          maxImages={maxImages}
          maxSize={
            enableStockPhotoPicker ? STOCK_PHOTO_MAX_IMAGE_SIZE : maxImageSize
          }
          label={label}
          stockEnabled={enableStockPhotoPicker && (isLibrary || !!tripId)}
          isLibrary={isLibrary}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </Box>
    );
  }

  // File/Attachment upload field
  if (type === 'file') {
    return (
      <Box>
        <LineItemAttachmentManager
          attachments={value || []}
          onChange={handleChange}
          onError={handleError}
          disabled={disabled}
          maxAttachments={maxAttachments}
          maxSize={maxFileSize}
          label={label}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </Box>
    );
  }

  // Unknown field type
  return null;
};

LineItemFieldRenderer.propTypes = {
  fieldDef: PropTypes.shape({
    field: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    required: PropTypes.bool,
    accordion: PropTypes.bool
  }).isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  expanded: PropTypes.bool,
  onAccordionChange: PropTypes.func,
  tripId: PropTypes.number,
  tenantId: PropTypes.number,
  isLibrary: PropTypes.bool
};

export default LineItemFieldRenderer;
